import React, { useState } from 'react';

import classNames from 'classnames';
import moment from 'moment';
import Mustache from 'mustache';
import { connect } from 'react-redux';

import { IconButton } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';

import { userSelector } from '../../reducers/current-user-selectors';

import { UiButton } from '../../ui/ui-button';

import { FormattedMessageBody } from './formatted-message-body';
import { InlineContactFile } from './inline-contact-file';
import { LegacyTranslateMessageButton } from './legacy-translate-message-button';
import { TwilioErrorDetailDialog } from './text-message-errors/twilio-error-detail-dialog';

import { LanguageCode, LANGUAGES } from '../../util/languages';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { VCardMessage } from '../../widgets/contact-card/v-card-message';
import { FilesDisplay } from '../../widgets/message-composer/toolbar/files-display';
import { AnnouncementStyling } from '../utils/announcement-styling';
import { useSentMessageTranslation } from '../utils/use-sent-message-translation';

const styles = () =>
  createStyles({
    text: {
      borderRadius: '15px',
      position: 'relative',
    },
    textImage: {
      maxWidth: '400px',
      margin: '0 auto',
      cursor: 'pointer',
    },
    requiresResponse: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      width: '20px',
      height: '20px',
      fontSize: '1rem',
    },
    errorContainer: {
      display: 'flex',
      alignItems: 'center',
      // 30px is the size of a small icon button
      marginLeft: '-30px',
    },
  });

const mapStateToProps = (state: any) => ({
  user: userSelector(state),
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type Props = ReduxProps &
  WithStyles<typeof styles> & {
    messageBody: string;

    className?: string;

    messageSid?: string;
    senderName?: string;
    sendDateTime?: string | Date;
    timeZone?: string;
    imageUrls?: string;
    otherLanguages?: Array<{ languageCode: LanguageCode; messageBody: string }>;
    requiresResponse?: boolean | null;
    announcement?: Announcement | null;

    disableOtherLanguagesMessage?: boolean;
    isAnnouncement?: boolean;
    isInbound?: boolean;
    isAutomated?: boolean;
    isFailed?: boolean;
    includesHTML?: boolean;
    doNotTrim?: boolean;
    allowLinkToAnnouncement?: boolean;
    forceAllowEditing?: boolean;
    urlFileMappings?: Array<UrlFileMapping>;
    sentWithVCard?: boolean;
    orgId?: number;
    urlVisitDetails?: { [pathLowerCased: string]: VisitDetail };
    maxCharacters?: number;
    isScheduledToSend?: boolean;

    onEditMessage?: () => void;
    onCancelMessage?: () => void;
    onDeleteMessage?: () => void;
    announcementGroupLabelOnClick?: () => void;

    contactFilesWithUrls?: { contactFile: ContactFile; presignedUrl: string }[];
    messageIdAndLanguageCode?: MessageIdAndLanguageCodeType;

    dataCy?: string;
  };

export const TextMessage = connect(mapStateToProps)(
  withStyles(styles)(
    ({
      classes,
      messageSid,
      isInbound,
      isAutomated,
      imageUrls,
      senderName,
      sendDateTime,
      timeZone,
      messageBody,
      user,
      requiresResponse,
      isFailed,
      otherLanguages,
      onEditMessage,
      onCancelMessage,
      className,
      onDeleteMessage,
      includesHTML,
      doNotTrim,
      maxCharacters,
      disableOtherLanguagesMessage,
      announcement,
      isAnnouncement,
      forceAllowEditing,
      allowLinkToAnnouncement,
      urlFileMappings,
      announcementGroupLabelOnClick,
      sentWithVCard,
      orgId,
      isScheduledToSend,
      urlVisitDetails,
      contactFilesWithUrls,
      dataCy,
      messageIdAndLanguageCode,
    }: Props) => {
      const {
        disableTranslateButton,
        disableTranslateButtonMessage,
        shouldShowTranslation,
        hideTranslation,
        loading: translationLoading,
        translatedMessageBody,
        onTranslateMessageClick,
      } = useSentMessageTranslation({
        messageId: messageIdAndLanguageCode?.messageId,
        messageBody,
      });

      isAnnouncement = isAnnouncement || !!announcement;
      const { announcementGroupLabel, backgroundClass } = AnnouncementStyling({
        announcement,
        allowLinkToAnnouncement,
        isAnnouncement,
        isInbound,
        isAutomated,
        announcementGroupLabelOnClick,
      });

      const [showFullMessage, setShowFullMessage] = useState<boolean>(false);
      const [showMessageFailureDialog, setShowMessageFailureDialog] = useState(false);
      // only used for outbound bulk messages sent in multiple languages
      const [displayLanguage, setDisplayLanguage] = useState<LanguageCode>('en');

      const openImageInNewTab = (imageUrl: string) => () => {
        window.open(imageUrl);
      };
      const handleShowFullMessageClick = () => setShowFullMessage(!showFullMessage);

      const handleMessageFailureClick = () => setShowMessageFailureDialog(true);
      const handleMessageFailureClose = () => setShowMessageFailureDialog(false);

      const onOtherLanguageClick = (languageCode: LanguageCode) => () => {
        setDisplayLanguage(languageCode);
      };

      const allLanguages = (otherLanguages || [])?.concat({
        languageCode: 'en',
        messageBody,
      });
      const hiddenLanguages = allLanguages.filter(
        ({ languageCode }) => languageCode !== displayLanguage,
      );

      let messageBodyToUse;
      // outbound bulk message sent in multiple languages
      if (!isInbound && otherLanguages) {
        messageBodyToUse =
          allLanguages.find(({ languageCode }) => languageCode === displayLanguage)?.messageBody ||
          messageBody;
      } else {
        messageBodyToUse =
          shouldShowTranslation && translatedMessageBody ? translatedMessageBody : messageBody;
      }

      // only show previews for the files referred to in the message
      const urlKeysInMessage = Mustache.parse(messageBodyToUse).reduce<Array<string>>(
        (acc, spans) => [...acc, spans[1]],
        [],
      );
      const urlMappingsForLanguage = urlFileMappings?.filter((m) =>
        urlKeysInMessage.includes(m.urlKey),
      );

      const didTruncateMessage =
        maxCharacters !== undefined && messageBodyToUse.length > maxCharacters;
      const hasSpanAddedToMessage = didTruncateMessage && !showFullMessage;
      const truncatedMessage = hasSpanAddedToMessage
        ? `${messageBodyToUse.slice(0, maxCharacters)}<span>...</span>`
        : messageBodyToUse;
      const isEditAllowed =
        forceAllowEditing || !sendDateTime || moment(sendDateTime).isAfter(moment());

      return (
        <div className="u-margin-y">
          <div className={isFailed ? classes.errorContainer : ''}>
            <div
              className={classNames(
                classes.text,
                backgroundClass,
                isFailed && 'u-flexGrow-1',
                className,
                'u-padding',
              )}
            >
              {senderName ? (
                <div className="typo-subtitle">
                  {senderName} on {moment(sendDateTime).format('dddd, MMMM Do [at] h:mma')}{' '}
                  {timeZone}
                  {announcementGroupLabel()}
                </div>
              ) : null}
              {sentWithVCard ? (
                <VCardMessage orgId={orgId} isScheduledMessage={isScheduledToSend} />
              ) : null}
              <div className="u-flexDirection-column">
                <FormattedMessageBody
                  className="u-margin-y-sm u-overflowY-auto u-maxHeight-150px u-wordBreak-breakWord u-whiteSpace-preWrap"
                  messageBody={truncatedMessage}
                  includesHTML={hasSpanAddedToMessage ? true : includesHTML}
                  shouldTrim={!doNotTrim}
                  urlVisitDetails={urlVisitDetails}
                  dataCy={dataCy}
                />
                {messageIdAndLanguageCode?.messageId && (
                  <LegacyTranslateMessageButton
                    isTranslated={shouldShowTranslation}
                    onTranslateMessageClick={onTranslateMessageClick}
                    loading={translationLoading}
                    onShowOriginalClick={hideTranslation}
                    languageCode={messageIdAndLanguageCode.messageLanguageCode}
                    disabled={disableTranslateButton}
                    disabledTooltip={disableTranslateButtonMessage}
                  />
                )}
              </div>
              <FilesDisplay urlFileMappings={urlMappingsForLanguage} isPreview />
              {/* NOTE: this UI does not extend nicely to the case where messages
                  were sent in more than 2 languages. We should clean this up when
                  supporting sending messages in more than 2 languages */}
              {!disableOtherLanguagesMessage &&
                otherLanguages &&
                hiddenLanguages.map(({ languageCode }) => (
                  <div key={languageCode} className="typo-subtitle">
                    <span
                      role="presentation"
                      className="typo-link"
                      onClick={onOtherLanguageClick(languageCode)}
                      data-cy={`view-language-${languageCode}`}
                    >
                      View {LANGUAGES[languageCode]} version
                    </span>
                  </div>
                ))}
              {imageUrls &&
                imageUrls
                  .split(',')
                  .map((url) => (
                    <img
                      alt="sent in text message"
                      role="presentation"
                      key={url}
                      src={url}
                      onClick={openImageInNewTab(url)}
                      className={classes.textImage}
                    />
                  ))}
              {contactFilesWithUrls?.map(({ contactFile, presignedUrl }) => (
                <InlineContactFile
                  key={`contact-file-${contactFile.contactFileId}`}
                  contactFile={contactFile}
                  presignedUrl={presignedUrl}
                />
              ))}
              {user.isAdmin && requiresResponse && (
                <Avatar variant="circle" className={classes.requiresResponse}>
                  R
                </Avatar>
              )}
              {isEditAllowed && (onEditMessage || onCancelMessage) ? (
                <div className={didTruncateMessage ? 'u-spread' : ''}>
                  {didTruncateMessage ? (
                    <div
                      className="typo-link"
                      role="presentation"
                      onClick={handleShowFullMessageClick}
                    >
                      {showFullMessage ? 'Show Less...' : 'Show More...'}
                    </div>
                  ) : null}
                  <div className="u-display-flex u-justifyContent-flexEnd">
                    {onEditMessage ? (
                      <UiButton
                        variant="tertiary"
                        size="xs"
                        onClick={onEditMessage}
                        dataCy="text-edit"
                      >
                        Edit
                      </UiButton>
                    ) : null}
                    {onCancelMessage ? (
                      <UiButton
                        variant="tertiary"
                        size="xs"
                        onClick={onCancelMessage}
                        className="u-margin-left-md"
                      >
                        Cancel
                      </UiButton>
                    ) : null}
                    {onDeleteMessage ? (
                      <UiButton
                        variant="tertiary"
                        size="xs"
                        onClick={onDeleteMessage}
                        className="u-margin-left-md"
                      >
                        Delete
                      </UiButton>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
            {isFailed && (
              <div>
                <IconButton
                  className="u-color-red"
                  size="small"
                  onClick={handleMessageFailureClick}
                  disabled={!messageSid}
                >
                  <MaterialDesignIcon iconName={ICON_NAMES.ERROR} />
                </IconButton>
              </div>
            )}
          </div>
          {isFailed && (
            <div className="u-display-flex u-justifyContent-flexEnd u-margin-right-2x">
              <div
                className={`u-color-red ${messageSid ? 'u-cursor-pointer u-hover-underline' : ''}`}
                role="presentation"
                onClick={messageSid ? handleMessageFailureClick : undefined}
              >
                Not Delivered
              </div>
            </div>
          )}
          {messageSid && showMessageFailureDialog && (
            <TwilioErrorDetailDialog messageSid={messageSid} onClose={handleMessageFailureClose} />
          )}
        </div>
      );
    },
  ),
);
